import React, {useState, useEffect, useMemo} from "react";
import { useNavigate } from "react-router-dom"; 
import Menu from "../../../../../components/layout/menu";
import SurveyHistoryTable from "../../../../../components/tables/surveys/surveyHistory";
import details from "../../../../../assets/images/icons/tables/details.svg"
import DownloadDocumentButton from "../../../../../components/shared/downloadDocument";
import { getClosedSurveys, softDeleteSurvey, updateSurvey, downloadFinishedSurveys, getSurveyById } from "../../../../../client/surveys";
import ModalDelete from "../../../../../components/shared/modals/modalDelete";
import ModalSuccess from "../../../../../components/shared/modals/modalSuccess";
import Pagination from "../../../../../components/shared/pagination";
import ContentLoader from "react-content-loader";
import EditDateSurvey from "../../../../../components/shared/modals/editDateSurvey";

export default function SurveyHistory() {
  const navigate = useNavigate(); 
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [showModalSuccess, setShowModalSuccess] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [downloading, setDownloading] = useState(false); 

  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

const [showModalEditDate, setShowModalEditDate] = useState(false);
const [surveyToEditDate, setSurveyToEditDate] = useState(null);

  useEffect(() => {
    fetchSurveys();
  }, [currentPage, recordsPerPage, searchTerm]);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const response = await getClosedSurveys(currentPage, recordsPerPage, searchTerm);
      if (response.data && response.data.closedSurveys) {
        setSurveys(response.data.closedSurveys);
        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.totalCount || 0);
      }
    } catch (error) {
      console.error("Error fetching surveys:", error);
      setSurveys([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await downloadFinishedSurveys();
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `Encuestas_cerradas_${new Date().toISOString().slice(0, 10)}.xlsx`;
      
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1];
        }
      }
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error("Error downloading surveys:", error);
      setSuccessMessage("Error al descargar el archivo.");
      setShowModalSuccess(true);
    } finally {
      setDownloading(false);
    }
  };

  const handleDetailsClick = (surveyId, surveyTitle) => {
    navigate(`/respuestas-encuesta/${surveyId}`, { 
      state: { surveyTitle }
    });
  };

  const handleDeleteClick = (surveyId, surveyTitle) => {
    setSurveyToDelete({ id: surveyId, title: surveyTitle });
    setShowModalDelete(true);
  };

  const handleResendClick = async (surveyId, surveyTitle) => {
    try {
      setLoading(true);
      const response = await getSurveyById(surveyId);
      
      if (response.data && response.data.survey) {
        navigate('/crear-encuesta', { 
          state: { 
            duplicateSurveyData: response.data.survey,
            originalSurveyTitle: surveyTitle,
            isDuplicating: true
          }
        });
      }
    } catch (error) {
      console.error("Error fetching survey for duplicate:", error);
      setSuccessMessage("Error al cargar los datos de la encuesta para duplicar.");
      setShowModalSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!surveyToDelete) return;

    try {
      const response = await softDeleteSurvey(surveyToDelete.id);
      
      if (response.status === 200) {
        setSuccessMessage("Encuesta eliminada correctamente.");
        setShowModalSuccess(true);
        
        await fetchSurveys();
      }
    } catch (error) {
      console.error("Error deleting survey:", error);
      setSuccessMessage("Error al eliminar la encuesta.");
      setShowModalSuccess(true);
    } finally {
      setShowModalDelete(false);
      setSurveyToDelete(null);
    }
  };

  const closeModalDelete = () => {
    setShowModalDelete(false);
    setSurveyToDelete(null);
  };

  const closeModalSuccess = () => {
    setShowModalSuccess(false);
  };

const handleEditDateClick = (surveyId, surveyTitle, currentClosingDate, fechaFinalOriginal) => {
  setSurveyToEditDate({ 
    id: surveyId, 
    title: surveyTitle,
    closing_At: fechaFinalOriginal 
  });
  setShowModalEditDate(true);
};

const handleConfirmEditDate = async (surveyId, newClosingDate) => {
  try {
    const surveyResponse = await getSurveyById(surveyId);
    const surveyData = surveyResponse.data.survey;
    
    const updateData = {
      Title: surveyData.title,
      Description: surveyData.description,
      OpeningAt: surveyData.opening_At,
      ClosingAt: newClosingDate,
      Questions: surveyData.questions || [],
      UserIds: surveyData.assignedUsers?.map(user => user.id) || []
    };
    
    const response = await updateSurvey(surveyId, updateData);
    
    if (response.status === 200) {
      setSuccessMessage("Fecha de cierre actualizada correctamente.");
      setShowModalSuccess(true);
      await fetchSurveys(); 
      setShowModalEditDate(false);
      setSurveyToEditDate(null);
    }
  } catch (error) {
    console.error("Error updating survey closing date:", error);
    setSuccessMessage("Error al actualizar la fecha de cierre.");
    setShowModalSuccess(true);
    
    setShowModalEditDate(false);
    setSurveyToEditDate(null);
  }
};

const closeModalEditDate = () => {
  setShowModalEditDate(false);
  setSurveyToEditDate(null);
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  
  try {
    const date = new Date(dateString + 'T00:00:00Z'); 
    return date.toLocaleDateString('es-ES', { 
      timeZone: 'UTC' 
    }); 
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; 
  }
};

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRecordsCountChange = (e) => {
    const newRecordsPerPage = parseInt(e.target.value);
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1); 
  };

  const TableContentLoader = () => {
    const rows = 8;
    const rowHeight = 62;
    const columnWidths = [200, 116, 123, 70, 141, 99, 99, 99]; 
    const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
    
    const getXPositions = () => {
      const positions = [0];
      for (let i = 1; i < columnWidths.length; i++) {
        positions.push(positions[i - 1] + columnWidths[i - 1]);
      }
      return positions;
    };

    const xPositions = getXPositions();

    return (
      <div className="overflow-x-auto ">
        <ContentLoader 
          speed={2}
          width={totalWidth}
          height={rows * rowHeight}
          viewBox={`0 0 ${totalWidth} ${rows * rowHeight}`}
          backgroundColor="#f3f3f3"
          foregroundColor="#ecebeb"
        >
          
          {Array(rows).fill('').map((_, rowIndex) => (
            columnWidths.map((width, colIndex) => (
              <rect 
                key={`${rowIndex}-${colIndex}`}
                x={xPositions[colIndex] + 15} // +15 para padding
                y={rowIndex * rowHeight + 25}
                rx="4" 
                ry="4" 
                width={width - 30} // -30 para margen
                height="20"
              />
            ))
          ))}
        </ContentLoader>
      </div>
    );
  };

  const tableData = useMemo(() => {
    return surveys.map(survey => ({
      id: survey.id,
      encuesta: survey.title,
      fechaInicio: formatDate(survey.opening_At),
      fechaFinal: formatDate(survey.closing_At),
      fechaFinalOriginal: survey.closing_At,
      cantRespuesta: survey.responses?.toString() || "0",
      realizador: survey.creator?.name,
      detalles: true,
      reenviar: "↻",
      eliminar: "💬"
    }));
  }, [surveys]);

  const tableColumns = useMemo(() => [
    {
      Header: "Encuesta",
      accessor: "encuesta",
      minWidth: 200
    },
    {
      Header: "Fecha de inicio",
      accessor: "fechaInicio",
      minWidth: 116
    },
    {
      Header: "Fecha final",
      accessor: "fechaFinal",
      minWidth: 123
    },
    {
      Header: "Cant. de respuesta",
      accessor: "cantRespuesta",
      minWidth: 70
    },
    {
      Header: "Realizador",
      accessor: "realizador",
      minWidth: 141
    },
    {
      Header: "Detalles",
      accessor: "detalles",
      minWidth: 99,
      Cell: ({ row }) => (
        <div className="flex items-center justify-center">
            <button 
              onClick={() => handleDetailsClick(row.original.id, row.original.encuesta)}
              className="p-2 flex justify-center items-center transition-colors bg-[#EFEEF7] hover:bg-gray-300 rounded-[4px] border-[#D9D9D9] border-[1px] w-[49px] h-[37px]">
                <img src={details} alt="Detalles" />
            </button>
        </div>
      )
    },
    {
      Header: "Aplazar",
      accessor: "aplazar",
      minWidth: 99,
      Cell: ({ row }) => (
        <div className="flex items-center justify-center">
              <button
                onClick={() => handleEditDateClick(
                  row.original.id, 
                  row.original.encuesta,
                  row.original.fechaFinal,
                  row.original.fechaFinalOriginal
                )}
                className="p-2 flex justify-center items-center bg-[#EFEEF7] rounded-[4px] border-[#D9D9D9] border-[1px] w-[49px] h-[37px] hover:bg-gray-300 transition-colors">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.333 16.048L16.57 3.81004C17.0534 3.34741 17.6988 3.09245 18.3679 3.09976C19.037 3.10707 19.6767 3.37608 20.1499 3.84917C20.6231 4.32226 20.8923 4.96183 20.8998 5.63093C20.9073 6.30003 20.6525 6.94547 20.19 7.42904L7.951 19.667C7.6718 19.9463 7.31619 20.1366 6.929 20.214L3 21L3.786 17.07C3.86345 16.6828 4.05378 16.3272 4.333 16.048Z" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M14.5 6.5L17.5 9.5" stroke="black" stroke-width="2"/>
                  </svg>
              </button>
        </div>
      )
    },
    {
      Header: "Duplicar",
      accessor: "reenviar",
      minWidth: 99,
      Cell: ({ row }) => (
        <div className="flex items-center justify-center">
            <button
                onClick={() => handleResendClick(row.original.id, row.original.encuesta)}
                className="group p-2 flex justify-center items-center bg-[#EFEEF7] rounded-[4px] border-[#D9D9D9] border-[1px] w-[49px] h-[37px] hover:bg-gray-300 hover:text-white transition-colors"
                title="Crear encuesta con los mismos datos"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM17.19 15.94C17.15 16.03 17.1 16.11 17.03 16.18L15.34 17.87C15.19 18.02 15 18.09 14.81 18.09C14.62 18.09 14.43 18.02 14.28 17.87C13.99 17.58 13.99 17.1 14.28 16.81L14.69 16.4H9.1C7.8 16.4 6.75 15.34 6.75 14.05V12.28C6.75 11.87 7.09 11.53 7.5 11.53C7.91 11.53 8.25 11.87 8.25 12.28V14.05C8.25 14.52 8.63 14.9 9.1 14.9H14.69L14.28 14.49C13.99 14.2 13.99 13.72 14.28 13.43C14.57 13.14 15.05 13.14 15.34 13.43L17.03 15.12C17.1 15.19 17.15 15.27 17.19 15.36C17.27 15.55 17.27 15.76 17.19 15.94ZM17.25 11.72C17.25 12.13 16.91 12.47 16.5 12.47C16.09 12.47 15.75 12.13 15.75 11.72V9.95C15.75 9.48 15.37 9.1 14.9 9.1H9.31L9.72 9.5C10.01 9.79 10.01 10.27 9.72 10.56C9.57 10.71 9.38 10.78 9.19 10.78C9 10.78 8.81 10.71 8.66 10.56L6.97 8.87C6.9 8.8 6.85 8.72 6.81 8.63C6.73 8.45 6.73 8.24 6.81 8.06C6.85 7.97 6.9 7.88 6.97 7.81L8.66 6.12C8.95 5.83 9.43 5.83 9.72 6.12C10.01 6.41 10.01 6.89 9.72 7.18L9.31 7.59H14.9C16.2 7.59 17.25 8.65 17.25 9.94V11.72Z" fill="#292D32"/>
              </svg>
            </button>
        </div>
      )
    },
    {
      Header: "Eliminar",
      accessor: "eliminar",
      minWidth: 99,
      Cell: ({ row }) => (
        <div className="flex items-center justify-center">
            <button
                onClick={() => handleDeleteClick(row.original.id, row.original.encuesta)}
                className="group p-2 flex justify-center items-center bg-[#EFEEF7] rounded-[4px] border-[#D9D9D9] border-[1px] w-[49px] h-[37px] hover:bg-red hover:text-white transition-colors">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.7008 3.10083L12.945 4.79167H16.25C16.4158 4.79167 16.5747 4.85751 16.6919 4.97472C16.8092 5.09193 16.875 5.25091 16.875 5.41667C16.875 5.58243 16.8092 5.7414 16.6919 5.85861C16.5747 5.97582 16.4158 6.04167 16.25 6.04167H15.6092L14.8817 14.5292C14.8375 15.0458 14.8017 15.4708 14.7442 15.8142C14.6858 16.1717 14.5967 16.4917 14.4225 16.7883C14.149 17.2543 13.7424 17.6278 13.255 17.8608C12.945 18.0083 12.6183 18.0692 12.2567 18.0975C11.9092 18.125 11.4833 18.125 10.965 18.125H9.035C8.51667 18.125 8.09083 18.125 7.74333 18.0975C7.38167 18.0692 7.055 18.0083 6.745 17.8608C6.25757 17.6278 5.85098 17.2543 5.5775 16.7883C5.4025 16.4917 5.315 16.1717 5.25583 15.8142C5.19833 15.47 5.1625 15.0458 5.11833 14.5292L4.39083 6.04167H3.75C3.58424 6.04167 3.42527 5.97582 3.30806 5.85861C3.19085 5.7414 3.125 5.58243 3.125 5.41667C3.125 5.25091 3.19085 5.09193 3.30806 4.97472C3.42527 4.85751 3.58424 4.79167 3.75 4.79167H7.055L7.29917 3.10083L7.30833 3.05C7.46 2.39167 8.025 1.875 8.73333 1.875H11.2667C11.975 1.875 12.54 2.39167 12.6917 3.05L12.7008 3.10083ZM8.3175 4.79167H11.6817L11.4683 3.31167C11.4283 3.1725 11.3267 3.125 11.2658 3.125H8.73417C8.67333 3.125 8.57167 3.1725 8.53167 3.31167L8.3175 4.79167ZM9.375 8.75C9.375 8.58424 9.30915 8.42527 9.19194 8.30806C9.07473 8.19085 8.91576 8.125 8.75 8.125C8.58424 8.125 8.42527 8.19085 8.30806 8.30806C8.19085 8.42527 8.125 8.58424 8.125 8.75V12.9167C8.125 13.0824 8.19085 13.2414 8.30806 13.3586C8.42527 13.4758 8.58424 13.5417 8.75 13.5417C8.91576 13.5417 9.07473 13.4758 9.19194 13.3586C9.30915 13.2414 9.375 13.0824 9.375 12.9167V8.75ZM11.875 8.75C11.875 8.58424 11.8092 8.42527 11.6919 8.30806C11.5747 8.19085 11.4158 8.125 11.25 8.125C11.0842 8.125 10.9253 8.19085 10.8081 8.30806C10.6908 8.42527 10.625 8.58424 10.625 8.75V12.9167C10.625 13.0824 10.6908 13.2414 10.8081 13.3586C10.9253 13.4758 11.0842 13.5417 11.25 13.5417C11.4158 13.5417 11.5747 13.4758 11.6919 13.3586C11.8092 13.2414 11.875 13.0824 11.875 12.9167V8.75Z"
                 fill="currentColor"
                 className="transition-colors group-hover:fill-white"/>
                </svg>
            </button>
        </div>
      )
    }
  ], []);
  
  return (
    <div>
      <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300" />
        <div className={`flex-1 pt-[67px] transition-all duration-300 ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"} bg-[#F7F8F9] w-full z-50 min-h-screen`}>
            <div className="sm:pt-8 pt-5 pb-10 sm:px-[3%] px-[5%] flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h1 className="text-blue font-itcbold text-2xl md:text-[32px]">
                  Encuestas Finalizadas
                </h1>
                <DownloadDocumentButton 
                  text="Descargar"
                  onClick={handleDownload}
                  loading={downloading}
                  hasData={surveys.length > 0}
                  disabled={loading && surveys.length < 0}
                />
              </div>
              {loading ? (
                <TableContentLoader />
              ) : (
                <div>
                  <div className="flex items-center justify-between my-2">
                    <div className="flex items-center space-x-2 text-sm font-itcbook">
                      <p >Mostrar</p>
                      <div className="border-[#D1D5DB] border-[1px] rounded-md w-[58px] flex justify-center items-center h-[40px]">
                        <select
                          className="w-full h-full px-2 text-sm bg-white rounded-md outline-none cursor-pointer font-itcbook"
                          value={recordsPerPage}
                          onChange={handleRecordsCountChange}  
                        >
                          <option value={10}>10</option>
                          <option value={15}>15</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>
                      </div>
                      <p >Registros</p>
                    </div>
                  </div>
                  <div className="w-full mt-4 overflow-hidden overflow-x-auto">
                    <SurveyHistoryTable 
                      columns={tableColumns}
                      data={tableData}
                      className="mt-4"
                      emptyMessage="No hay encuestas finalizadas"
                    />
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      changePage={handlePageChange}
                      recordsPerPage={recordsPerPage}
                      totalRecords={totalCount}
                    />
                  </div>
                </div>
              )}
            </div>
        </div>

        <ModalDelete 
          showModalDelete={showModalDelete}
          closeModal={closeModalDelete}
          accept={handleConfirmDelete}
          textType="encuesta"
          pronoun="la"
        />

        <ModalSuccess 
          showModalSuccess={showModalSuccess}
          closeModalSuccess={closeModalSuccess}
          fill="green" 
          text={successMessage}
        />

        <EditDateSurvey 
          showModal={showModalEditDate}
          closeModal={closeModalEditDate}
          survey={surveyToEditDate}
          onConfirm={handleConfirmEditDate}
        />
    </div>
  );
}